import React, { createContext, useContext, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const TenantContext = createContext(null);

/**
 * Provides current tenant info resolved from the URL :tenantId param.
 * Validates the user has access to this tenant before rendering children.
 */
export function TenantProvider({ children }) {
  const { tenantId } = useParams();
  const { user } = useAuth();

  const tenantAccess = useMemo(() => {
    if (!user || !tenantId) return null;
    const list = user.tenant_access || [];
    return list.find((e) => e.tenant_id === tenantId) || null;
  }, [user, tenantId]);

  const value = useMemo(() => ({
    tenantId,
    role: tenantAccess?.role || null,
    hasAccess: !!tenantAccess,
    isAdmin: tenantAccess?.role === 'ADMIN',
    isViewer: tenantAccess?.role === 'VIEWER',
  }), [tenantId, tenantAccess]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}

/**
 * Wrapper route that checks tenant access before rendering.
 * Shows a clean "no access" page if the user isn't in this tenant.
 */
export function TenantGate({ children }) {
  const { hasAccess, tenantId } = useTenant();
  const { user } = useAuth();

  if (!hasAccess) {
    // Find user's first accessible tenant for redirect
    const firstTenant = (user?.tenant_access || [])[0]?.tenant_id;
    if (firstTenant) {
      return <Navigate to={`/${firstTenant}`} replace />;
    }
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="tenant-access-denied">
        <div className="text-center space-y-3">
          <p className="text-sm font-medium text-foreground">Access Restricted</p>
          <p className="text-xs text-muted-foreground">
            You don't have access to the <span className="font-medium">{tenantId}</span> workspace.
          </p>
        </div>
      </div>
    );
  }

  return children;
}
